import { useEffect, useRef } from 'react';
import { useEditor, docIsDirty, docHasContent } from '../context/EditorContext';
import { usePublicationActions } from './usePublicationActions';
import { saveProject } from '../lib/projectApi';
import { capturePublicationThumbnail } from '../lib/sheetRender';

/** Quiet save this long after the last change (typing included) — "3 seconds idle". */
export const AUTOSAVE_IDLE_MS = 3000;
/** Safety net / background-tab cadence. */
export const AUTOSAVE_INTERVAL_MS = 60 * 1000;
/** Page-1 thumbnails (Home / Open lists) are refreshed at most this often by autosave — rendering one costs a frame or two. */
const THUMBNAIL_EVERY_MS = 45 * 1000;
/** `localStorage['wp.autosaveIntervalMs']` (≥ 3 s) shortens the interval cadence for automated tests only. */
export const autosaveIntervalMs = () => {
  try {
    const v = Number(window.localStorage.getItem('wp.autosaveIntervalMs'));
    if (Number.isFinite(v) && v >= 3000) return v;
  } catch { /* storage unavailable */ }
  return AUTOSAVE_INTERVAL_MS;
};
export const autosaveIdleMs = () => {
  try {
    const v = Number(window.localStorage.getItem('wp.autosaveIdleMs'));
    if (Number.isFinite(v) && v >= 500) return v;
  } catch { /* storage unavailable */ }
  return AUTOSAVE_IDLE_MS;
};

/**
 * Cloud autosave — nothing typed is ever lost:
 *  - the active publication is saved quietly 3 s after the last change, also while a text box is being edited (the
 *    document state already holds every keystroke); only an object drag / resize / rotate postpones it;
 *  - a publication that was never saved is created automatically under its current name ("Untitled Publication") as
 *    soon as it has any content, so it shows up in File ▸ Open like any other — Save As renames it later;
 *  - background tabs that have a project id are saved every minute from their snapshot;
 *  - a one-minute tick also retries failed saves. Reports through `state.autosaveStatus` (status-bar chip);
 *    nothing is ever marked saved unless the server confirmed it.
 */
export const useAutosave = () => {
  const { state, stateRef, rawDispatch } = useEditor();
  const { saveToCloud } = usePublicationActions();
  const running = useRef(false);
  const lastThumbAt = useRef(0);
  const saveToCloudRef = useRef(saveToCloud);
  saveToCloudRef.current = saveToCloud;

  const tickRef = useRef(null);
  tickRef.current = async ({ background = false } = {}) => {
    if (running.current) return;
    const s = stateRef.current;
    if (!s.autosave || s.isSaving) return;
    running.current = true;
    let count = 0, failed = 0;
    try {
      // active publication — skip while the user is mid-drag so we never persist a half-made move
      const dirty = s.docRevision !== s.savedRevision;
      const creatable = !!s.projectId || docHasContent(s);
      if (dirty && creatable && !s.interacting) {
        const withThumbnail = Date.now() - lastThumbAt.current > THUMBNAIL_EVERY_MS;
        const saved = await saveToCloudRef.current({ quiet: true, thumbnail: withThumbnail });
        if (saved) { count += 1; if (withThumbnail) lastThumbAt.current = Date.now(); } else failed += 1;
      }
      // background tabs — their snapshot is complete by definition (read the tabs afresh: the active save took time)
      if (background) {
        for (const t of stateRef.current.docTabs) {
          if (t.id === stateRef.current.activeTabId || !t.doc?.projectId || !docIsDirty(t.doc)) continue;
          const { id: tabId } = t;
          const { projectId, docRevision } = t.doc;
          try {
            const saved = await saveProject(t.doc, { name: t.doc.projectName, thumbnail: await capturePublicationThumbnail(t.doc) });
            rawDispatch({ type: 'MARK_TAB_SAVED', payload: { tabId, projectId, revision: docRevision, savedAt: saved?.updated_at } });
            count += 1;
          } catch { failed += 1; }
        }
      }
    } finally {
      running.current = false;
      if (count || failed) rawDispatch({ type: 'SET_AUTOSAVE_STATUS', payload: { at: Date.now(), count, failed } });
    }
  };

  // Idle save: (re)armed on every document change; a drag in progress re-arms it when the drag ends.
  const dirty = state.docRevision !== state.savedRevision;
  useEffect(() => {
    if (!state.autosave || !dirty || state.interacting) return undefined;
    const id = setTimeout(() => tickRef.current(), autosaveIdleMs());
    return () => clearTimeout(id);
  }, [state.autosave, dirty, state.docRevision, state.interacting]);

  // Cadence: background tabs + retry of anything the idle save could not persist.
  useEffect(() => {
    if (!state.autosave) return undefined;
    const id = setInterval(() => tickRef.current({ background: true }), autosaveIntervalMs());
    return () => clearInterval(id);
  }, [state.autosave]);
};
